import { useState } from "react";
import { useTranslation } from "react-i18next";
import { hrApi, usePayslip, usePeriods, useRun } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { formatDateTime, money } from "../../lib/format";
import type { CalcSummary, HrLevel, PayrollPeriod } from "../../lib/typesHr";
import { DownloadButton } from "../ui/DownloadButton";
import { PrintButton } from "../ui/PrintButton";
import { Empty, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { dateOnly, Field, Kpi, mins, ReasonDialog, StatusPill, Table, todayIso, useAction, useEmpName } from "./shared";

export function PayslipDialog({ id, onClose, level }: { id: string; onClose: () => void; level: HrLevel }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const d = usePayslip(id);
  const { act } = useAction();
  const [voiding, setVoiding] = useState(false);
  const p = d.data?.payslip as { number: string; status: string; gross_fils: number; total_deductions_fils: number; net_fils: number; period_label: string; period_start: string; period_end: string; full_name: string; full_name_ar: string | null } | undefined;
  const lines = d.data?.lines ?? [];
  const section = (dir: "earning" | "deduction" | "employer") => lines.filter((l) => l.direction === dir);
  return (
    <Modal open size="lg" title={p ? `${t("hr.payslip.title")} ${p.number}` : t("hr.payslip.title")} onClose={onClose}>
      {p && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-black">{locale === "ar" && p.full_name_ar ? p.full_name_ar : p.full_name} <span className="text-xs font-semibold text-gray-500">· {p.period_label} (<span className="num">{dateOnly(p.period_start, locale)} → {dateOnly(p.period_end, locale)}</span>)</span></div>
            <StatusPill status={p.status} />
          </div>
          {(["earning", "deduction"] as const).map((dir) => (
            <Table key={dir} head={[t(dir === "earning" ? "hr.payslip.earnings" : "hr.payslip.deductions"), t("hr.qty"), t("hr.loans.amount")]}>
              {section(dir).map((l) => <tr key={l.line_no}><td>{locale === "ar" && l.description_ar ? l.description_ar : l.description}</td><td className="num">{l.quantity ?? ""}</td><td className="num font-bold">{money(l.amount_fils)}</td></tr>)}
              {section(dir).length === 0 && <tr><td colSpan={3} className="text-gray-400">—</td></tr>}
            </Table>
          ))}
          <div className="grid grid-cols-3 gap-3">
            <Kpi label={t("hr.payslip.gross")} value={money(p.gross_fils)} />
            <Kpi label={t("hr.payslip.totalDeductions")} value={money(p.total_deductions_fils)} tone="red" />
            <Kpi label={t("hr.payslip.net")} value={money(p.net_fils)} tone="green" />
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex gap-2">
              <DownloadButton path={`/hr/payslips/${id}/pdf?locale=${locale}`} fallbackName={`payslip-${p.number}.pdf`} className="btn-dark">📄 {t("hr.payslip.pdf")}</DownloadButton>
              <PrintButton path={`/hr/payslips/${id}/pdf?locale=${locale}`} className="btn-ghost" />
            </div>
            {level >= 3 && ["approved", "paid"].includes(p.status) && <button type="button" className="btn-ghost text-red-700" onClick={() => setVoiding(true)}>{t("hr.payslip.void")}</button>}
          </div>
          <ReasonDialog open={voiding} title={t("hr.payslip.void")} confirmLabel={t("hr.payslip.void")} onClose={() => setVoiding(false)} onConfirm={(reason) => { setVoiding(false); void act(() => hrApi.post(`/payslips/${id}/void`, { reason }), "hr.done").then(onClose); }} />
        </>
      )}
    </Modal>
  );
}

function RunView({ id, level, onBack }: { id: string; level: HrLevel; onBack: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const name = useEmpName();
  const q = useRun(id);
  const { act, busy } = useAction();
  const [summary, setSummary] = useState<CalcSummary | null>(null);
  const [slip, setSlip] = useState<string | null>(null);
  const [method, setMethod] = useState("bank_transfer");
  const run = q.data?.run;
  const canWrite = level >= 3;

  if (!run) return <div className="card p-5 text-xs font-bold text-gray-500">{t("common.loading")}</div>;
  return (
    <section className="card space-y-4 p-5" aria-label={t("hr.payroll.run")}>
      <SectionHeader icon="🧾" title={`${t("hr.payroll.run")} · ${run.label}`} subtitle={`${dateOnly(run.period_start, locale)} → ${dateOnly(run.period_end, locale)}`}
        right={<div className="flex items-center gap-2"><StatusPill status={run.status} ns="runStatus" /><button type="button" className="btn-ghost" onClick={onBack}>← {t("hr.back")}</button></div>} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label={t("hr.payroll.employees")} value={run.employee_count} />
        <Kpi label={t("hr.payslip.gross")} value={money(run.gross)} />
        <Kpi label={t("hr.payslip.totalDeductions")} value={money(run.deductions)} tone="red" />
        <Kpi label={t("hr.payslip.net")} value={money(run.net)} tone="green" hint={`${t("hr.payroll.employerCost")}: ${money(run.employer_cost)}`} />
      </div>
      <div className="text-[11px] font-semibold text-gray-500">
        {run.calculated_at && <span>{t("hr.payroll.calculatedBy", { name: run.calculated_by_name ?? "—" })} · {formatDateTime(run.calculated_at, locale)}</span>}
        {run.approved_at && <span> · {t("hr.payroll.approvedBy", { name: run.approved_by_name ?? "—" })}</span>}
      </div>

      {canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          {["draft", "calculated"].includes(run.status) && <button type="button" className="btn-primary" disabled={busy} onClick={() => void act(() => hrApi.post<CalcSummary>(`/payroll/runs/${id}/calculate`)).then((r) => r && setSummary(r))}>{run.status === "draft" ? t("hr.payroll.calculate") : t("hr.payroll.recalculate")}</button>}
          {run.status === "calculated" && <button type="button" className="btn-success" disabled={busy} onClick={() => { if (window.confirm(t("hr.payroll.approveConfirm", { net: money(run.net) }))) void act(() => hrApi.post(`/payroll/runs/${id}/approve`), "hr.payroll.approved"); }}>{t("hr.payroll.approve")}</button>}
          {run.status === "approved" && (
            <>
              <select className="field !w-auto" value={method} onChange={(e) => setMethod(e.target.value)} aria-label={t("hr.paymentMethod")}>{["bank_transfer", "cash", "cliq", "cheque"].map((m) => <option key={m} value={m}>{t(`hr.pay.${m}`)}</option>)}</select>
              <button type="button" className="btn-success" disabled={busy} onClick={() => void act(() => hrApi.post(`/payroll/runs/${id}/pay`, { method }), "hr.payroll.paid")}>{t("hr.payroll.pay")}</button>
            </>
          )}
          {run.status !== "draft" && (
            <>
              <DownloadButton path={`/hr/payroll/runs/${id}/register.pdf?locale=${locale}`} fallbackName="payroll.pdf">📄 {t("hr.payroll.register")}</DownloadButton>
              <DownloadButton path={`/hr/payroll/runs/${id}/transfers.xlsx?locale=${locale}`} fallbackName="transfers.xlsx">📊 {t("hr.payroll.transfers")}</DownloadButton>
            </>
          )}
        </div>
      )}

      {summary && (summary.skipped.length > 0 || summary.warnings.length > 0) && (
        <div className="space-y-1 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-900" role="status">
          {summary.skipped.map((s) => <div key={s.employeeId}>⚠️ <b>{s.employeeNo}</b> {s.name}: {s.reason}</div>)}
          {summary.warnings.map((w, i) => <div key={i}>ℹ️ <b>{w.employeeNo}</b>: {t(`hr.payroll.warning.${w.warning.split(":")[0]}`, { defaultValue: w.warning })}</div>)}
        </div>
      )}

      <Table head={[t("hr.employee"), t("hr.payroll.base"), t("hr.payroll.overtime"), t("hr.payroll.bonus"), t("hr.payslip.totalDeductions"), t("hr.payslip.net"), t("hr.status.label")]} empty={q.data!.payslips.length === 0 ? <Empty>{t("hr.payroll.noPayslips")}</Empty> : undefined}>
        {q.data!.payslips.filter((p) => p.status !== "void").map((p) => (
          <tr key={p.id} className="cursor-pointer hover:bg-purple-50/40" onClick={() => setSlip(p.id)}>
            <td className="font-black">{name(p)} <span className="num font-mono text-[10px] text-gray-400">{p.employee_no}</span>{p.warnings && p.warnings.length > 0 && <span title={p.warnings.join(", ")}> ⚠️</span>}</td>
            <td className="num">{money(p.base_fils)}<div className="text-[10px] text-gray-400">{p.pay_type === "hourly" ? `${mins(p.worked_minutes)} h` : p.absent_days ? `${p.absent_days} ${t("hr.payroll.absentDays")}` : ""}</div></td>
            <td className="num">{money(p.overtime_fils)}</td><td className="num">{money(p.bonuses_fils)}</td>
            <td className="num text-red-700">{money(p.total_deductions_fils)}</td><td className="num font-black">{money(p.net_fils)}</td>
            <td><StatusPill status={p.status} /></td>
          </tr>
        ))}
      </Table>
      {slip && <PayslipDialog id={slip} level={level} onClose={() => setSlip(null)} />}
    </section>
  );
}

export function PayrollPanel({ level }: { level: HrLevel }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const periods = usePeriods(level >= 2);
  const { act, busy } = useAction();
  const [runId, setRunId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState(todayIso().slice(0, 7));
  const [start, setStart] = useState(`${todayIso().slice(0, 7)}-01`);
  const [end, setEnd] = useState(todayIso());

  if (runId) return <RunView id={runId} level={level} onBack={() => setRunId(null)} />;

  async function newRun(p: PayrollPeriod) {
    const r = await act(() => hrApi.post<{ id: string }>(`/payroll/periods/${p.id}/runs`, {}));
    if (r) setRunId(r.id);
  }
  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.tabs.payroll")}>
      <SectionHeader icon="💰" title={t("hr.tabs.payroll")} subtitle={t("hr.payroll.subtitle")} right={level >= 3 ? <button type="button" className="btn-primary" onClick={() => setAdding(true)}>{t("hr.payroll.newPeriod")}</button> : undefined} />
      <Table head={[t("hr.payroll.period"), t("hr.period"), t("hr.status.label"), t("hr.payroll.runs"), ""]} empty={(periods.data ?? []).length === 0 ? <Empty>{t("hr.payroll.none")}</Empty> : undefined}>
        {(periods.data ?? []).map((p) => (
          <tr key={p.id}>
            <td className="font-black">{p.label}</td>
            <td className="num">{dateOnly(p.period_start, locale)} → {dateOnly(p.period_end, locale)}</td>
            <td><StatusPill status={p.status} ns="periodStatus" /></td>
            <td>{(p.runs ?? []).map((r) => <button key={r.id} type="button" className="btn-ghost me-1 !px-2 !py-0.5" onClick={() => setRunId(r.id)}><StatusPill status={r.status} ns="runStatus" /> <span className="num">{money(r.net)}</span></button>)}</td>
            <td className="whitespace-nowrap text-end">
              {level >= 3 && !["approved", "paid", "closed"].includes(p.status) && !(p.runs ?? []).some((r) => r.status !== "void") && <button type="button" className="btn-primary" disabled={busy} onClick={() => void newRun(p)}>{t("hr.payroll.startRun")}</button>}
              {level >= 3 && p.status === "paid" && <button type="button" className="btn-dark" disabled={busy} onClick={() => { if (window.confirm(t("hr.payroll.closeConfirm"))) void act(() => hrApi.post(`/payroll/periods/${p.id}/close`), "hr.done"); }}>{t("hr.payroll.close")}</button>}
            </td>
          </tr>
        ))}
      </Table>
      <Modal open={adding} title={t("hr.payroll.newPeriod")} onClose={() => setAdding(false)} size="sm"
        footer={<><button type="button" className="btn-ghost" onClick={() => setAdding(false)}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={busy || label.trim().length < 3 || end < start} onClick={() => void act(() => hrApi.post("/payroll/periods", { label: label.trim(), periodStart: start, periodEnd: end }), "hr.saved").then((r) => { if (r) setAdding(false); })}>{t("hr.save")}</button></>}>
        <Field label={t("hr.payroll.period")}><input className="field num" value={label} onChange={(e) => setLabel(e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("hr.from")}><input type="date" className="field num" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label={t("hr.to")}><input type="date" className="field num" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
        </div>
      </Modal>
    </section>
  );
}
